import { useReducer, useCallback } from "react";
import type { GameState, HexCoord, Move, Puzzle } from "../types.ts";
import { applyMove, cloneGrid } from "../engine/cascade.ts";
import { calculateStars } from "../engine/scoring.ts";

type GameAction =
  | { type: "DROP"; coord: HexCoord }
  | { type: "CASCADE_TICK" }
  | { type: "UNDO" }
  | { type: "RESET" }
  | { type: "NEW_PUZZLE"; puzzle: Puzzle };

function initGameState(puzzle: Puzzle): GameState {
  return {
    puzzle,
    currentGrid: cloneGrid(puzzle.grid),
    moveHistory: [],
    phase: "playing",
    startedAt: Date.now(),
    stars: 0,
    cascadeAnimation: null,
  };
}

// We store grid snapshots for undo (grids are small, this is fine)
let gridSnapshots: ReturnType<typeof cloneGrid>[] = [];

function gameReducer(state: GameState, action: GameAction): GameState {
  switch (action.type) {
    case "DROP": {
      if (state.phase !== "playing") return state;
      if (state.cascadeAnimation) return state;

      const result = applyMove(state.currentGrid, action.coord);
      if (!result.success) return state;

      gridSnapshots.push(cloneGrid(state.currentGrid));

      const newHistory: Move[] = [...state.moveHistory, { coord: action.coord }];
      const won = result.won;
      const lost = !won && newHistory.length >= state.puzzle.maxMoves;

      const hasCascade = result.cascadeSteps.length > 0;

      return {
        ...state,
        currentGrid: result.finalGrid,
        moveHistory: newHistory,
        phase: won ? "won" : lost ? "lost" : "playing",
        completedAt: won || lost ? Date.now() : undefined,
        stars: won ? calculateStars(newHistory.length, state.puzzle) : 0,
        cascadeAnimation: hasCascade ? {
          frames: result.intermediateGrids,
          overflows: result.cascadeSteps,
          currentFrame: 0,
        } : null,
      };
    }

    case "CASCADE_TICK": {
      if (!state.cascadeAnimation) return state;
      const next = state.cascadeAnimation.currentFrame + 1;
      if (next >= state.cascadeAnimation.frames.length) {
        return { ...state, cascadeAnimation: null };
      }
      return {
        ...state,
        cascadeAnimation: { ...state.cascadeAnimation, currentFrame: next },
      };
    }

    case "UNDO": {
      if (state.moveHistory.length === 0 || state.phase !== "playing") return state;
      if (state.cascadeAnimation) return state;

      const prevGrid = gridSnapshots.pop();
      if (!prevGrid) return state;

      return {
        ...state,
        currentGrid: prevGrid,
        moveHistory: state.moveHistory.slice(0, -1),
        cascadeAnimation: null,
      };
    }

    case "RESET": {
      gridSnapshots = [];
      return {
        ...state,
        currentGrid: cloneGrid(state.puzzle.grid),
        moveHistory: [],
        phase: "playing",
        startedAt: Date.now(),
        cascadeAnimation: null,
      };
    }

    case "NEW_PUZZLE": {
      gridSnapshots = [];
      return initGameState(action.puzzle);
    }

    default:
      return state;
  }
}

export function useGame(puzzle: Puzzle) {
  const [state, dispatch] = useReducer(gameReducer, puzzle, (p) => {
    gridSnapshots = [];
    return initGameState(p);
  });

  const drop = useCallback((coord: HexCoord) => dispatch({ type: "DROP", coord }), []);
  const cascadeTick = useCallback(() => dispatch({ type: "CASCADE_TICK" }), []);
  const undo = useCallback(() => dispatch({ type: "UNDO" }), []);
  const reset = useCallback(() => dispatch({ type: "RESET" }), []);
  const newPuzzle = useCallback((p: Puzzle) => dispatch({ type: "NEW_PUZZLE", puzzle: p }), []);

  return { state, drop, cascadeTick, undo, reset, newPuzzle };
}
